<template>
  <div class="reg-panel">
    <div class="panel-body">
      <div class="row g-3 align-items-start">
        <!-- Columna izquierda -->
        <div class="col-8">
          <!-- Carga de archivo -->
          <div class="search-box">
            <label class="field-label">Archivo CSV (columna "numero" con los N° de planilla)</label>
            <div class="d-flex gap-1">
              <input
                ref="inputArchivo"
                type="file"
                accept=".csv"
                class="search-input"
                @change="onArchivoChange"
              />
              <button
                type="button"
                class="btn-reload"
                @click="$refs.inputArchivo.click()"
                title="Seleccionar archivo"
              >
                <span class="material-symbols-outlined" style="font-size:18px">upload_file</span>
              </button>
            </div>
            <div class="search-status mt-1">
              <span v-if="archivoCsv" class="text-muted">
                <span class="material-symbols-outlined" style="font-size:14px; vertical-align:-2px">description</span>
                {{ archivoCsv.name }}
                <span v-if="lineasCsv !== null">— {{ lineasCsv }} fila(s) detectada(s)</span>
              </span>
              <span v-else class="text-muted">Ningún archivo seleccionado</span>
            </div>
          </div>

          <!-- Log de resultados (RAISE NOTICE del procedimiento) -->
          <div v-if="mensajes.length" class="tabla-seleccionados mt-3">
            <div class="tabla-header" style="grid-template-columns: 1fr;">
              <span>
                {{ ultimaEjecucionSimulada ? 'Resultado de la simulación' : 'Resultado de la ejecución' }}
              </span>
            </div>
            <div class="log-box">
              <div v-for="(m, i) in mensajes" :key="i" class="log-line">{{ m }}</div>
            </div>
          </div>
          <div v-else class="tabla-vacia mt-3">
            <span class="material-symbols-outlined" style="font-size:28px; color:#ced4da">upload_file</span>
            <p class="mt-1 mb-0">Seleccione un CSV y ejecute la vista previa</p>
          </div>
        </div>

        <!-- Columna derecha: acciones -->
        <div class="col-4 d-flex flex-column gap-3" style="padding-top:2rem;">
          <div class="resumen-box">
            <div class="resumen-row">
              <span>Trámite</span>
              <strong class="text-primary">{{ idTramite }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.planillasCsv !== null">
              <span>Planillas en CSV</span>
              <strong>{{ resumen.planillasCsv }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.detalles !== null">
              <span>Detalles {{ ultimaEjecucionSimulada ? 'a anular' : 'anulados' }}</span>
              <strong>{{ resumen.detalles }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.planillas !== null">
              <span>Planillas {{ ultimaEjecucionSimulada ? 'a anular' : 'anuladas' }}</span>
              <strong>{{ resumen.planillas }}</strong>
            </div>
            <div class="resumen-row" v-if="resumen.errores !== null">
              <span>Errores</span>
              <strong :class="resumen.errores > 0 ? 'text-danger' : ''">{{ resumen.errores }}</strong>
            </div>
          </div>

          <button
            type="button" class="btn-action secondary"
            @click="ejecutar(true)"
            :disabled="!archivoCsv || procesando"
            title="Previsualizar sin persistir cambios"
          >
            <span class="material-symbols-outlined">visibility</span>
            Vista previa (simular)
          </button>

          <button
            type="button" class="btn-action primary"
            @click="confirmarEjecucionReal"
            :disabled="!puedeEjecutarReal"
            title="Ejecutar la anulación de forma definitiva"
          >
            <span class="material-symbols-outlined">block</span>
            Ejecutar definitivamente
          </button>

          <button
            type="button" class="btn-action secondary"
            @click="limpiar"
            title="Limpiar selección"
          >
            <span class="material-symbols-outlined">delete</span>
            Limpiar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "AnularLabImgPorTramite",
  props: {
    idTramite: { type: [Number, String], required: true },
    idUsuario: { type: [Number, String], required: true },
  },
  emits: ["ejecutado"],
  data() {
    return {
      archivoCsv: null,
      lineasCsv: null,
      procesando: false,
      mensajes: [],
      ultimaEjecucionSimulada: null, // true | false | null
      previewOk: false, // ya se corrió simulación exitosa con el archivo actual
      resumen: {
        planillasCsv: null,
        detalles: null,
        planillas: null,
        errores: null,
      },
    };
  },
  computed: {
    puedeEjecutarReal() {
      return (
        this.archivoCsv &&
        this.previewOk &&
        !this.procesando &&
        (this.resumen.errores === null || this.resumen.errores === 0)
      );
    },
  },
  methods: {
    onArchivoChange(e) {
      const file = e.target.files[0] || null;
      this.archivoCsv = file;
      this.previewOk = false;
      this.mensajes = [];
      this.resetResumen();
      this.lineasCsv = null;
      if (file) this.contarLineas(file);
    },
    contarLineas(file) {
      const reader = new FileReader();
      reader.onload = () => {
        const texto = String(reader.result || "");
        const filas = texto
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter((l) => l.length > 0);
        // Resta 1 por el encabezado si hay al menos una línea
        this.lineasCsv = Math.max(filas.length - 1, 0);
      };
      reader.readAsText(file, "ISO-8859-1"); // coherente con ENCODING LATIN1 del procedimiento
    },
    resetResumen() {
      this.resumen = { planillasCsv: null, detalles: null, planillas: null, errores: null };
    },
    parseResumen(mensajes) {
      const buscar = (regex) => {
        for (const m of mensajes) {
          const match = m.match(regex);
          if (match) return Number(match[1]);
        }
        return null;
      };
      this.resumen.planillasCsv = buscar(/Planillas en CSV\s*:\s*(\d+)/i);
      this.resumen.detalles = buscar(/Detalles\s*\S*\s*:\s*(\d+)/i);
      this.resumen.planillas = buscar(/Planillas\s*\S*\s*:\s*(\d+)\s*$/im);
      this.resumen.errores = buscar(/Errores\s*:\s*(\d+)/i);
    },
    async confirmarEjecucionReal() {
      const confirm = await Swal.fire({
        title: "¿Confirmar anulación definitiva?",
        html: `Se anularán <strong>${this.resumen.detalles ?? "?"}</strong> detalle(s) y
               <strong>${this.resumen.planillas ?? "?"}</strong> planilla(s) sin detalle activo
               en el trámite <strong>${this.idTramite}</strong>.<br><br>
               Esta acción <strong>no se puede deshacer</strong>.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, ejecutar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#dc3545",
      });
      if (!confirm.isConfirmed) return;
      await this.ejecutar(false);
    },
    async ejecutar(simular) {
      if (!this.archivoCsv) return;
      this.procesando = true;
      this.mensajes = [];
      try {
        const formData = new FormData();
        formData.append("idTramite", Number(this.idTramite));
        formData.append("idUsuario", Number(this.idUsuario));
        formData.append("simular", simular);
        formData.append("archivo", this.archivoCsv);

        const res = await api.post("/procedimientos/anular-lab-img", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        this.mensajes = res.data?.mensajes ?? [];
        this.ultimaEjecucionSimulada = simular;
        this.parseResumen(this.mensajes);

        if (simular) {
          this.previewOk = true;
          if ((this.resumen.errores ?? 0) > 0) {
            Swal.fire(
              "Simulación con errores",
              `Se detectaron ${this.resumen.errores} error(es). Revise el detalle antes de ejecutar.`,
              "warning"
            );
          } else {
            Swal.fire("Vista previa lista", "Revise el resumen y ejecute cuando esté listo.", "info");
          }
        } else {
          this.previewOk = false; // fuerza nueva simulación si se quiere repetir
          Swal.fire("Éxito", "Anulación ejecutada correctamente.", "success");
          this.$emit("ejecutado");
        }
      } catch (e) {
        const raw = e.response?.data;
        const msg = typeof raw === "string" ? raw : raw?.message ?? raw?.error ?? e.message ?? "Error desconocido";
        Swal.fire("Error", msg, "error");
      } finally {
        this.procesando = false;
      }
    },
    limpiar() {
      this.archivoCsv = null;
      this.lineasCsv = null;
      this.mensajes = [];
      this.previewOk = false;
      this.ultimaEjecucionSimulada = null;
      this.resetResumen();
      if (this.$refs.inputArchivo) this.$refs.inputArchivo.value = "";
    },
  },
};
</script>

<style scoped>
.reg-panel  { background: #f8f9fa; }
.panel-body { padding: 14px 16px; }
.search-input {
  width: 100%; padding: 6px 10px; border: 1px solid #ced4da;
  border-radius: 4px; font-size: 0.85rem; transition: border-color 0.15s;
}
.search-input:focus { outline: none; border-color: #0d6efd; box-shadow: 0 0 0 2px rgba(13,110,253,.15); }
.btn-reload {
  display: flex; align-items: center; justify-content: center;
  padding: 6px 10px; border: 1px solid #ced4da; border-radius: 4px;
  background: #fff; cursor: pointer; color: #495057; transition: all 0.15s;
}
.btn-reload:hover { background: #0d6efd; color: #fff; border-color: #0d6efd; }
.search-status { font-size: 0.78rem; }
.field-label { font-weight: 600; font-size: 0.85rem; color: #495057; display: block; margin-bottom: 4px; }
.tabla-seleccionados {
  border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden; font-size: 0.82rem;
}
.tabla-header {
  display: grid;
  background: #e9ecef; padding: 5px 8px; font-weight: 700; color: #495057; gap: 6px;
}
.log-box {
  max-height: 320px; overflow-y: auto; background: #fff;
  font-family: "Courier New", monospace; font-size: 0.76rem;
}
.log-line {
  padding: 3px 8px; border-top: 1px solid #f0f0f0; white-space: pre-wrap;
}
.tabla-vacia {
  display: flex; flex-direction: column; align-items: center;
  padding: 20px; color: #adb5bd; font-size: 0.85rem;
  border: 2px dashed #dee2e6; border-radius: 6px; background: #fff;
}
.resumen-box {
  background: #fff; border: 1px solid #dee2e6;
  border-radius: 6px; padding: 10px 12px; font-size: 0.82rem;
}
.resumen-row {
  display: flex; justify-content: space-between; padding: 2px 0; color: #495057;
}
.btn-action {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 14px; border: none; border-radius: 6px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s; width: 100%;
}
.btn-action.primary                      { background: #dc3545; color: #fff; }
.btn-action.primary:hover:not(:disabled) { background: #bb2d3b; }
.btn-action.secondary                    { background: #fff; color: #6c757d; border: 1px solid #dee2e6; }
.btn-action.secondary:hover:not(:disabled) { background: #f8f9fa; }
.btn-action:disabled                     { opacity: 0.45; cursor: not-allowed; }
</style>